import {useCallback} from 'react';
import type {
  MergeSuggestion,
  MergeSuggestionType,
} from 'services/mergeSuggestions/types';
import {useLazyGetAgentsByIdsQuery} from 'services/agent/agentApi';
import {useLazyGetWorksByIdsQuery} from 'services/metadata/workApi';
import {useLazyGetSeriesByIdsQuery} from 'services/series/seriesApi';
import {
  applyAgentTitlesToMergeSuggestions,
  applyCollectionTitlesToMergeSuggestions,
  applyWorkTitlesToMergeSuggestions,
} from '../functions';

export const usePopulateMergeSuggestionTitles = (type: MergeSuggestionType) => {
  const [getSeriesByIds] = useLazyGetSeriesByIdsQuery();
  const [getAgentsByIds] = useLazyGetAgentsByIdsQuery();
  const [getWorksByIds] = useLazyGetWorksByIdsQuery();

  return useCallback(
    async (data: MergeSuggestion[]): Promise<MergeSuggestion[]> => {
      if (data.length === 0) {
        return data;
      }

      const ids = [...data.map(d => d.sourceId), ...data.map(d => d.targetId)];
      if (ids.length === 0) {
        return data;
      }

      switch (type) {
        case 'SERIES': {
          const series = await getSeriesByIds(ids).unwrap();
          return applyCollectionTitlesToMergeSuggestions(data, series);
        }
        case 'AGENT': {
          const agents = await getAgentsByIds(ids).unwrap();
          return applyAgentTitlesToMergeSuggestions(data, agents);
        }
        case 'WORK': {
          const works = await getWorksByIds(ids).unwrap();
          return applyWorkTitlesToMergeSuggestions(data, works);
        }
        default: {
          return Promise.reject(`Unsupported merge suggestion type: ${type}`);
        }
      }
    },
    [type, getSeriesByIds, getAgentsByIds, getWorksByIds],
  );
};
